import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useAuth } from "../auth/AuthContext";
import {
  createUser,
  fetchUsers,
  resetPassword,
  updateUser,
  type AdminUser,
  type ApiResult,
  type CreatedUser,
} from "../lib/users";
import { ROLES, ROLE_LABELS, type Role } from "../lib/roles";
import { NotificationSettingsSection } from "./NotificationSettingsSection";
import { OrgProfileSection } from "./OrgProfileSection";
import { CalendarSection, KeyRatesSection } from "./ReferenceSections";

/**
 * Экран «Настройки → Пользователи» (E1 + E1a). Только для администратора: сервер проверяет право
 * `user:manage` на каждом маршруте (матрица 4.3), навигация лишь скрывает раздел (правило 5).
 * Оформление — токены docs/07 (правило 9): штампы ролей, логины и пароль — PT Mono.
 *
 * Возможности: список, создание, назначение ролей, деактивация; E1a — правка ФИО, кнопка сброса
 * пароля и одноразовый показ сгенерированного сервером стартового/нового пароля (пароль нигде не
 * сохраняется, повторно не показывается — только новый сброс). Справочники/реквизиты — E3/E15.
 */

/** Одноразовая выдача пароля для показа администратору (E1a): при создании или при сбросе. */
interface IssuedPassword {
  readonly login: string;
  readonly password: string;
  readonly kind: "create" | "reset";
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly users: AdminUser[] }
  | { readonly kind: "error"; readonly message: string };

/** Штамп роли. Роли — категории, не статусы: нейтральная пара токенов; админ выделен акцентом. */
function RoleStamp({ role }: { role: Role }) {
  const cls = role === "admin" ? "text-accent bg-control-bg" : "text-control-fg bg-control-bg";
  return <span className={`stamp ${cls}`}>{ROLE_LABELS[role]}</span>;
}

/** Штамп активности учётной записи: активна — «done», отключена — приглушённая «waiting». */
function ActiveStamp({ active }: { active: boolean }) {
  return active ? (
    <span className="stamp text-done-fg bg-done-bg">Активен</span>
  ) : (
    <span className="stamp text-waiting-fg bg-waiting-bg">Отключён</span>
  );
}

/** Группа чекбоксов ролей (совмещение допускается — В14). */
function RoleChecklist({
  idPrefix,
  selected,
  onToggle,
}: {
  idPrefix: string;
  selected: ReadonlySet<Role>;
  onToggle: (role: Role, checked: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {ROLES.map((role) => {
        const id = `${idPrefix}-${role}`;
        return (
          <label key={role} htmlFor={id} className="inline-flex items-center gap-1.5 text-[13px]">
            <input
              id={id}
              type="checkbox"
              checked={selected.has(role)}
              onChange={(e) => onToggle(role, e.target.checked)}
              className="accent-accent"
            />
            {ROLE_LABELS[role]}
          </label>
        );
      })}
    </div>
  );
}

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";

/**
 * Одноразовый показ пароля (E1a). Пароль виден единственный раз: администратор копирует его и
 * передаёт сотруднику защищённым способом (лично), не по почте/мессенджеру. В системе хранится
 * лишь хеш — восстановить пароль нельзя, только сбросить заново. Пароль держим только в состоянии
 * компонента (никакого localStorage): при закрытии карточки или перезагрузке он исчезает.
 */
function PasswordReveal({ issued, onClose }: { issued: IssuedPassword; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const title = issued.kind === "create" ? "Стартовый пароль создан" : "Пароль сброшен";

  // Показ единственный — переводим на карточку фокус и подматываем её в вид, чтобы пароль не
  // «потерялся» вверху страницы (частый случай: сброс пользователю в конце длинного списка).
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    el.focus();
    try {
      el.scrollIntoView({ block: "nearest" });
    } catch {
      // scrollIntoView не реализован в jsdom/старых браузерах — фокус уже поставлен, этого хватит.
    }
  }, [issued.password]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.password);
      setCopied(true);
    } catch {
      setCopied(false); // буфер обмена недоступен — пароль по-прежнему виден, можно переписать
    }
  };

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      role="alert"
      className="mb-4 rounded-[var(--radius-l)] border border-accent bg-control-bg p-4"
    >
      <h3 className="text-[14px] font-bold text-ink">
        {title} для «{issued.login}»
      </h3>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="num rounded-[var(--radius-m)] border border-border bg-surface px-3 py-1.5 text-[15px] font-bold tracking-wide text-ink">
          {issued.password}
        </code>
        <button type="button" onClick={() => void copy()} className={btnSecondary}>
          {copied ? "Скопировано" : "Скопировать"}
        </button>
        <button type="button" onClick={onClose} className={btnSecondary}>
          Закрыть
        </button>
      </div>
      <p className="mt-2 text-[13px] text-soon-fg">
        Пароль показывается один раз. Передайте его сотруднику лично или другим защищённым способом
        — не по почте и не в мессенджере. Повторно увидеть пароль нельзя, только сбросить заново.
      </p>
    </div>
  );
}

export function SettingsPage() {
  const { user: currentUser, logout } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Одноразовая выдача пароля (E1a): последняя генерация — при создании или сбросе.
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchUsers();
    if (res.ok) {
      setState({ kind: "ready", users: res.value });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [logout]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Заменяет одного пользователя в загруженном списке (после успешной правки). */
  const replaceUser = useCallback((updated: AdminUser) => {
    setState((prev) =>
      prev.kind === "ready"
        ? { kind: "ready", users: prev.users.map((u) => (u.id === updated.id ? updated : u)) }
        : prev,
    );
  }, []);

  const appendUser = useCallback((created: AdminUser) => {
    setState((prev) =>
      prev.kind === "ready"
        ? {
            kind: "ready",
            users: [...prev.users, created].sort((a, b) => a.login.localeCompare(b.login)),
          }
        : prev,
    );
  }, []);

  /** После создания: добавить пользователя в список и, если сервер выдал пароль, показать его. */
  const handleCreated = useCallback(
    (created: CreatedUser) => {
      appendUser(created.user);
      if (created.generatedPassword) {
        setIssued({
          login: created.user.login,
          password: created.generatedPassword,
          kind: "create",
        });
      }
    },
    [appendUser],
  );

  /** После сброса пароля: показать новый пароль один раз (обновление hasPassword — в строке). */
  const handlePasswordReset = useCallback((login: string, password: string) => {
    setIssued({ login, password, kind: "reset" });
  }, []);

  return (
    <section aria-labelledby="settings-title">
      <h1 id="settings-title" className="text-[20px] font-bold text-ink">
        Настройки
      </h1>
      <p className="mt-1 text-[13px] text-muted">
        Пользователи и роли, производственный календарь и ключевая ставка ЦБ. Реквизиты организации
        — раздел появится в эпике E15.
      </p>

      <h2 className="mt-6 mb-3 text-[16px] font-bold text-ink">Пользователи</h2>

      {issued && <PasswordReveal issued={issued} onClose={() => setIssued(null)} />}

      <CreateUserForm onCreated={handleCreated} />

      <div className="mt-6">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка списка пользователей…
          </p>
        )}

        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-5"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}

        {state.kind === "ready" && (
          <UsersTable
            users={state.users}
            currentUserId={currentUser?.id ?? null}
            onUpdated={replaceUser}
            onPasswordReset={handlePasswordReset}
          />
        )}
      </div>

      {/* Реквизиты организации-владельца инстанса (E15): шапка печатных форм. Стоят ПЕРВЫМИ
          среди настроек инстанса — это первое, что заполняется при развёртывании у заказчика,
          и без них печатная форма расчёта выходит с отметкой о незаполненных реквизитах. */}
      <OrgProfileSection />

      {/* Справочники (E3): производственный календарь и ключевая ставка ЦБ — ведёт администратор. */}
      <CalendarSection />
      <KeyRatesSection />
      {/* Параметры уведомлений и эскалации (E10, Прил. В) — тоже только администратор. */}
      <NotificationSettingsSection />
    </section>
  );
}

/**
 * Форма создания пользователя: логин, ФИО, роли. Пароль администратор не вводит — сервер
 * сгенерирует стойкий стартовый пароль и покажет его один раз после создания (E1a).
 */
function CreateUserForm({ onCreated }: { onCreated: (created: CreatedUser) => void }) {
  const [login, setLogin] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<Set<Role>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRole = useCallback((role: Role, checked: boolean) => {
    setRoles((prev) => {
      const next = new Set(prev);
      if (checked) next.add(role);
      else next.delete(role);
      return next;
    });
  }, []);

  const reset = () => {
    setLogin("");
    setFullName("");
    setRoles(new Set());
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await createUser({
      login: login.trim(),
      fullName: fullName.trim(),
      roles: [...roles],
      generatePassword: true,
    });
    setBusy(false);
    if (res.ok) {
      onCreated(res.value);
      reset();
      return;
    }
    setError(res.message);
  };

  const inputCls =
    "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";

  return (
    <form
      onSubmit={onSubmit}
      aria-labelledby="create-user-title"
      className="max-w-[var(--w-form)] rounded-[var(--radius-l)] border border-border bg-surface p-4"
    >
      <h3 id="create-user-title" className="mb-3 text-[14px] font-bold text-ink">
        Новый пользователь
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="cu-login" className="mb-1 block text-[13px] text-muted">
            Логин
          </label>
          <input
            id="cu-login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="off"
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="cu-name" className="mb-1 block text-[13px] text-muted">
            ФИО
          </label>
          <input
            id="cu-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={inputCls}
            required
          />
        </div>
      </div>

      <fieldset className="mt-3">
        <legend className="mb-1.5 text-[13px] text-muted">Роли (можно совмещать)</legend>
        <RoleChecklist idPrefix="cu-role" selected={roles} onToggle={toggleRole} />
      </fieldset>

      <p className="mt-3 text-[13px] text-muted">
        Стартовый пароль сгенерируется автоматически и будет показан один раз после создания.
      </p>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Создание…" : "Создать пользователя"}
        </button>
      </div>
    </form>
  );
}

/** Таблица пользователей с правкой ФИО/ролей, деактивацией и сбросом пароля. */
function UsersTable({
  users,
  currentUserId,
  onUpdated,
  onPasswordReset,
}: {
  users: AdminUser[];
  currentUserId: string | null;
  onUpdated: (u: AdminUser) => void;
  onPasswordReset: (login: string, password: string) => void;
}) {
  if (users.length === 0) {
    return <p className="text-muted">Пользователей пока нет.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-l)] border border-border bg-surface">
      <table className="w-full border-collapse text-[14px]">
        <caption className="sr-only">Список пользователей системы</caption>
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted">
            <th scope="col" className="px-4 py-2.5 font-bold">
              ФИО / логин
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Роли
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Статус
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Действия
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={u.id === currentUserId}
              onUpdated={onUpdated}
              onPasswordReset={onPasswordReset}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onUpdated,
  onPasswordReset,
}: {
  user: AdminUser;
  isSelf: boolean;
  onUpdated: (u: AdminUser) => void;
  onPasswordReset: (login: string, password: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftRoles, setDraftRoles] = useState<Set<Role>>(new Set(user.roles));
  const [draftName, setDraftName] = useState(user.fullName);
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraftRoles(new Set(user.roles));
    setDraftName(user.fullName);
    setError(null);
    setEditing(true);
  };

  const toggleRole = (role: Role, checked: boolean) => {
    setDraftRoles((prev) => {
      const next = new Set(prev);
      if (checked) next.add(role);
      else next.delete(role);
      return next;
    });
  };

  const apply = async (patch: Parameters<typeof updateUser>[1], after?: () => void) => {
    setBusy(true);
    setError(null);
    const res: ApiResult<AdminUser> = await updateUser(user.id, patch);
    setBusy(false);
    if (res.ok) {
      onUpdated(res.value);
      after?.();
      return;
    }
    setError(res.message);
  };

  // Сохранение правки: шлём только изменившиеся поля. Так правка одного ФИО не трогает роли —
  // не берёт серверный admin-guard (advisory-блокировку) и не пишет в аудит мнимое «изменение».
  const saveEdits = () => {
    const trimmed = draftName.trim();
    if (trimmed.length === 0) {
      setError("Укажите ФИО пользователя.");
      return;
    }
    const rolesUnchanged =
      draftRoles.size === user.roles.length && user.roles.every((r) => draftRoles.has(r));
    const patch: { fullName?: string; roles?: Role[] } = {};
    if (trimmed !== user.fullName) patch.fullName = trimmed;
    if (!rolesUnchanged) patch.roles = [...draftRoles];
    if (patch.fullName === undefined && patch.roles === undefined) {
      setEditing(false); // ничего не изменилось — просто выходим из режима правки
      return;
    }
    void apply(patch, () => setEditing(false));
  };

  // Сброс пароля с серверной генерацией: новый пароль показываем один раз (E1a). Сброс отзывает
  // прежние сессии пользователя, поэтому просим подтверждение (двухшаговая кнопка).
  const doReset = async () => {
    setBusy(true);
    setError(null);
    const res = await resetPassword(user.id);
    setBusy(false);
    setConfirmReset(false);
    if (res.ok) {
      onPasswordReset(user.login, res.value.generatedPassword);
      // Сброс всегда задаёт пароль и всегда поднимает флаг D-15 — строка должна показать
      // оба факта сразу, не дожидаясь перезагрузки списка.
      onUpdated({ ...user, hasPassword: true, mustChangePassword: true });
      return;
    }
    setError(res.message);
  };

  // Два разных состояния пароля, и путать их нельзя: «не задан» — войти вообще нельзя;
  // «ждёт смены» (D-15) — войти можно, но кроме смены пароля человеку ничего не доступно.
  const passwordWarn = !user.hasPassword ? (
    <span className="ml-2 text-[12px] text-soon-fg" title="Пароль не задан — вход недоступен">
      пароль не задан
    </span>
  ) : user.mustChangePassword ? (
    <span
      className="ml-2 text-[12px] text-muted"
      title="Выданный пароль ещё не сменён: до смены доступны только экран смены пароля и выход (D-15)"
    >
      ждёт смены пароля
    </span>
  ) : null;

  const nameInputCls =
    "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2 py-1 text-[14px]";

  return (
    <tr className="border-b border-border-soft align-top last:border-b-0">
      <td className="px-4 py-3">
        {editing ? (
          <input
            aria-label={`ФИО — ${user.login}`}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className={nameInputCls}
            required
          />
        ) : (
          <div className="text-ink">{user.fullName}</div>
        )}
        <div className="num mt-1 text-[12px] text-muted">{user.login}</div>
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <RoleChecklist
            idPrefix={`edit-${user.id}-role`}
            selected={draftRoles}
            onToggle={toggleRole}
          />
        ) : user.roles.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {user.roles.map((role) => (
              <RoleStamp key={role} role={role} />
            ))}
          </div>
        ) : (
          <span className="text-[13px] text-muted">роли не назначены</span>
        )}
      </td>
      <td className="px-4 py-3">
        <ActiveStamp active={user.isActive} />
        {passwordWarn}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <button type="button" disabled={busy} className={btnPrimary} onClick={saveEdits}>
                Сохранить
              </button>
              <button
                type="button"
                disabled={busy}
                className={btnSecondary}
                onClick={() => setEditing(false)}
              >
                Отмена
              </button>
            </>
          ) : confirmReset ? (
            <>
              <span className="text-[13px] text-ink">Сбросить пароль и отозвать сессии?</span>
              <button
                type="button"
                disabled={busy}
                className={btnPrimary}
                onClick={() => void doReset()}
              >
                Подтвердить сброс
              </button>
              <button
                type="button"
                disabled={busy}
                className={btnSecondary}
                onClick={() => setConfirmReset(false)}
              >
                Отмена
              </button>
            </>
          ) : (
            <>
              <button type="button" disabled={busy} className={btnSecondary} onClick={startEdit}>
                Изменить
              </button>
              <button
                type="button"
                disabled={busy || isSelf}
                className={btnSecondary}
                title={isSelf ? "Нельзя изменить активность своей учётной записи" : undefined}
                onClick={() => void apply({ isActive: !user.isActive })}
              >
                {user.isActive ? "Деактивировать" : "Активировать"}
              </button>
              <button
                type="button"
                disabled={busy || isSelf}
                className={btnSecondary}
                title={isSelf ? "Свой пароль меняйте через смену пароля, а не сброс" : undefined}
                onClick={() => setConfirmReset(true)}
              >
                Сбросить пароль
              </button>
            </>
          )}
        </div>
        {error && (
          <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}
